import { ChangeEvent, FC, useMemo, useState } from 'react';
import {
  Box,
  Stack,
  Table,
  TableContainer,
  TablePagination,
  Typography,
} from '@mui/material';
import { DataCollectionDto, type UserRole } from '@contract';
import { getFixturesCollectionsDateRange } from './utils';
import { FixturesCollectionsTableHeader } from './FixturesCollectionsTableHeader';
import { FixturesCollectionsTableBody } from './FixturesCollectionsTableBody';
import {
  FixturesCollectionsTableTitle,
  FixturesCollectionsType,
} from './constants';

export type SingleRoleDataCollection = DataCollectionDto & {
  role: UserRole;
};

interface FixturesCollectionsTableProps {
  type: FixturesCollectionsType;
  collections: DataCollectionDto[];
  isLoading: boolean;
}

export const FixturesCollectionsTable: FC<FixturesCollectionsTableProps> = ({
  type,
  collections,
  isLoading,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const singleRoleCollections = useMemo(
    () =>
      collections.reduce<SingleRoleDataCollection[]>(
        (acc: SingleRoleDataCollection[], collection: DataCollectionDto) => {
          if (collection.roles.length === 1) {
            acc.push({
              ...collection,
              role: collection.roles[0],
            });
          } else {
            const singleRoles = collection.roles.map((role) => ({
              ...collection,
              role,
            }));

            acc.push(...singleRoles);
          }

          return acc;
        },
        [],
      ),
    [collections],
  );

  const onPageChange = (_: unknown, pageNumber: number) => {
    setPage(pageNumber);
  };

  const onRowsPerPageChange = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Stack>
      <Box
        display='flex'
        flexDirection='row'
        justifyContent='space-between'
        p={2}
      >
        <Typography variant='h5'>
          {FixturesCollectionsTableTitle[type]}
        </Typography>
        <Typography variant='h6' align='right'>
          {getFixturesCollectionsDateRange(singleRoleCollections)}
        </Typography>
      </Box>
      <TableContainer id={`${type}-fixtures-table`}>
        <Table sx={{ minWidth: 900 }}>
          <FixturesCollectionsTableHeader />
          <FixturesCollectionsTableBody
            collections={singleRoleCollections.slice(
              page * rowsPerPage,
              page * rowsPerPage + rowsPerPage,
            )}
            isLoading={isLoading}
          />
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 15]}
        component='div'
        count={singleRoleCollections.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </Stack>
  );
};
